import React, { FC, useEffect, useState } from 'react';
import { TextButton, WixDesignSystemProvider } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common';
import { CompRef } from '@wix/document-services-types';
import { EditorPointer } from '@wix/editorx-core-api';
import actions from './actions';
import StyleLobbyList from './styleLobbyList';
import {
  GlobalDesignAction,
  GlobalDesignPanelProps,
  GlobalDesignStyleGroup,
} from './types';
import LobbyHeader from './common/lobby/LobbyHeader';
import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';

const getGroupedItems = async <T extends StylesDerivedNativeActionNames>(
  editorPointer: EditorPointer<CompRef>,
  action: GlobalDesignAction<T>,
  getHost: GlobalDesignPanelProps<T>['getHost'],
): Promise<GlobalDesignStyleGroup[]> => {
  const groupingStrategy = action.grouping.getGroupingStrategy(
    editorPointer,
    getHost,
  );

  return groupingStrategy();
};

export const GlobalDesignPanel: FC<
  GlobalDesignPanelProps<StylesDerivedNativeActionNames>
> = <T extends StylesDerivedNativeActionNames>(
  props: GlobalDesignPanelProps<T>,
) => {
  const action: GlobalDesignAction<T> = actions[props.actionName];

  const [groupedItems, setGropedItems] = useState<GlobalDesignStyleGroup[]>([]);
  const [selectedItem, setSelectedItem] =
    useState<GlobalDesignStyleGroup | null>(null);

  const isSingleGroupedItem = groupedItems.length === 1;

  useEffect(() => {
    (async () => {
      const _groupedItems = await getGroupedItems<T>(
        props.editorPointer,
        action,
        props.getHost,
      );

      if (_groupedItems.length === 1) {
        setSelectedItem(_groupedItems[0]);
      }

      setGropedItems(_groupedItems);
    })();
  }, [action, props.editorPointer, props.getHost]);

  const resetSelection = () => {
    setSelectedItem(null);
  };

  if (groupedItems.length === 0 || !action) {
    return null;
  }

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      {selectedItem ? (
        <>
          {!isSingleGroupedItem && (
            <TextButton
              prefixIcon={<ChevronLeft />}
              size="small"
              onClick={resetSelection}
            >
              Back to all styles
            </TextButton>
          )}
          {React.createElement(action.panel, { host: selectedItem.host })}
        </>
      ) : (
        <>
          <LobbyHeader
            actionName={props.actionName}
            stylesCount={groupedItems.length}
          />
          <StyleLobbyList
            items={groupedItems}
            action={action}
            onItemSelect={setSelectedItem}
          />
        </>
      )}
    </WixDesignSystemProvider>
  );
};
